import get from "lodash/get"
import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import { ReactSVG } from "react-svg"
import { wmFonts, wmColor } from "themes-lib/wmTheme"
import { makeStyles } from "tss-react/mui"
import { ChevronDown, ChevronUp } from "lucide-react"
import SiteSearchMobileHeader from "component-lib/SiteSearchMobileHeader/SiteSearchMobileHeader"
import getContentUrl from "utils-lib/getContentUrl"
import { getWindow } from "utils-lib/getWindow"
import Box from "component-lib/Base/components/Box"
import Typography from "component-lib/Base/components/Typography"
import { getFormattedAddressWithoutPostalCode } from "utils-lib/strings"
import { withLocalization } from "hoc-lib/localization"
import { isTeamsitePage } from "utils-lib/isTeamsitePage"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import withRouter from "hoc-lib/router"
import { MULTI_NAVIGATION_TABS_ITEMS } from "constants-lib/dashboardNavigationTabs"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import { useEffect, useState } from "react"
import { Link, Button } from "../../../../../Base"
import RedesignedHeaderNavItems from "../RedesignedHeaderNavItems/RedesignedHeaderNavItems"
import GlobalNotifications from "../../../GlobalNotifications/GlobalNotificationsContainer"
import SupportDropdown from "../../../TopHeader/components/SupportDropdown/SupportDropdownContainer"
import ProfileMenuModal from "../ProfileMenuModal/ProfileMenuModalContainer"

const useStyles = makeStyles()((theme, { height, isDashboardTabSelected }) => ({
  mobilePageWrapper: {
    height,
  },
  headerContainer: {
    position: "fixed",
    zIndex: "100",
    width: "100%",
    background: theme.palette.background.white,
    padding: "0px 24px",
  },
  subHeaderContainer: {
    height: "55px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px",
    marginTop: "",
  },
  headerLinksWrapper: {
    display: "flex",
    justifyContent: "space-around",
    width: "100%",
    "& > button": {
      color: `${theme.palette.text.secondary} !important`,
      height: "60px !important",
      padding: 0,
      ...wmFonts.maaxBold,
      ...{
        backgroundColor: theme.palette.secondary.title,
      },
    },
  },
  openHamburgerHeaderWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "14px",
  },
  closedHamburgerHeaderWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: {
    cursor: "auto",
    height: "28px",
  },

  iconWrapper: {
    padding: "0px",
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
  icon: {
    cursor: "auto",
    display: "flex",
    "& > svg": {
      height: `20px`,
      width: "100%",
    },
  },
  hamburgerClosedIcon: {
    display: "flex",
    alignItems: "center",
    marginRight: "16px",
    "& > svg": {
      height: 15,
      width: 20,
    },
    "& g": {
      stroke: wmColor.green1,
    },
  },
  hamburgerOpenIcon: {
    display: "flex",
    alignItems: "center",
    "& > svg": {
      height: 15,
      width: 20,
    },
    "& g": {
      stroke: wmColor.green1,
    },
  },
  rightMenuWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  cartButton: {
    position: "relative",
    padding: "0px",
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
  cartIcon: {
    cursor: "auto",
    display: "flex",
    alignItems: "center",
    "&>svg": {
      height: `18px`,
      width: "100%",
    },
  },
  counter: {
    width: "8px",
    height: "8px",
    position: "absolute",
    left: "10px",
    bottom: "10px",
    backgroundColor: wmColor.green6,
    borderRadius: "50%",
    fontSize: "6px",
    color: theme.palette.background.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...wmFonts.maaxBold,
  },
  wmLogo: {
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
  headerTab: {
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
  dashboardTab: {
    borderBottom: isDashboardTabSelected
      ? `2px solid black !important`
      : `2px solid ${theme.palette.secondary.title} !important`,
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
  billingTab: {
    borderBottom: !isDashboardTabSelected
      ? `2px solid black !important`
      : `2px solid ${theme.palette.secondary.title} !important`,
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
  customerSelectionRow: {
    border: "1px solid #E6E6E6",
    borderRadius: "8px",
    padding: "0.5rem 1rem",
    marginBottom: "1rem",
    height: "7vh",
  },
  dGrid: {
    display: "grid !important",
  },
  profileMenuModal: {
    width: "375px !important",
    padding: "1.5rem",
    paddingTop: "1rem",
    margin: "0px",
    alignItems: "start",
  },
  popupList: {
    padding: "0px",
    paddingTop: "1rem",
    minWidth: "287px",
    "a.wm-link": {
      fontSize: `1rem`,
      fontFamily: `Maax`,
      lineHeight: `1.5rem !important`,
      fontWeight: 500,
      color: `${wmColor.green8}!important`,
    },
    "& > li": {
      display: "flex",
      alignItems: "center",
      gap: "1rem",
      fontFamily: `Maax-Bold`,
      padding: "0",
      margin: "0",
    },
    display: "flex",
    flexDirection: "column",
    gap: ".8rem",
  },
  profileName: {
    fontSize: `1.5rem`,
    fontFamily: `Maax-Bold`,
    lineHeight: `1.8rem`,
    fontWeight: 700,
    margin: 0,
    paddingBottom: ".25rem",
  },
  profileEmail: {
    fontSize: `1rem`,
    fontFamily: `Maax`,
    lineHeight: `1.5rem`,
    fontWeight: 400,
    color: wmColor.gray11,
    margin: 0,
    paddingBottom: ".25rem",
  },
  menulogout: {
    width: "100%",
  },
  profileCloseModal: {
    top: "16px",
    right: "0px",
  },
  profileIcon: {
    paddingRight: "1rem",
    marginTop: ".5rem",
    "& svg": {
      width: "24px",
      height: "24px",
    },
  },
  customerSelectionTextBold: {
    fontWeight: "500 !important",
    lineHeight: "24px",
    color: `${theme.palette.text.black} !important`,
  },
}))

export const strings = {
  en_CA: {
    DASHBOARD: `Dashboard`,
    BILLING: `Billing`,
    SERVICES: `Services`,
    NOTIFICATIONS: `Notifications`,
    SUPPORT: `Support`,
    NEED_HELP: `Need Help?`,
    CART: `Cart`,
    SEARCH: `Search`,
    MY_SUPPORT_REQUESTS: `My Support Requests`,
  },
  fr_CA: {
    DASHBOARD: `Tableau de bord`,
    BILLING: `Facturation`,
    SERVICES: `Services`,
    NOTIFICATIONS: `Avis`,
    SUPPORT: `Soutien`,
    NEED_HELP: `Besoin d’aide?`,
    CART: `Panier`,
    SEARCH: `Search`,
    MY_SUPPORT_REQUESTS: `My Support Requests`,
  },
}

export const MobileRedesignedHeader = ({
  navigationDetails,
  siteLanguage,
  ecommerceCart = {},
  isMenuOpen,
  logo,
  logoLink,
  onMenuClick,
  setParentState,
  setShowMobileSearch,
  showMobileSearch,
  logoAltText,
  isSupportSearch,
  indexList,
  suggestionIndex,
  queryIndex,
  numResults,
  numHighlightedResults,
  numberQueries,
  numCharacters,
  errorMessageTitle,
  errorMessageDescription,
  failureTitle,
  failureDescription,
  suggestedText,
  resultsText,
  seeAllResultsText,
  searchDelay,
  resultsIndex,
  searchPlaceholder,
  hasNotification,
  router,
  strings,
  onCartClick,
  alerts,
  onAlertsChange,
  onAlertClose,
  isSingleAccount = false,
  firstName,
  emailId,
  mobilePhone,
  selectedAccountDetails,
  setIsCustomerSelectClicked,
  multiAccountDashboard,
  setNewMultiAccountTabSelected,
}) => {
  const ssrWindow = getWindow()

  const isGlobalNotificationClosed = JSON.parse(
    ssrWindow.localStorage.getItem("closeAll"),
  )
  const isAccountsDashboardPage = ssrWindow?.location?.pathname?.includes(
    "user/account-dashboard",
  )

  const isDashboardTabSelected =
    multiAccountDashboard?.multiAccountDashboardTabSelected ===
    MULTI_NAVIGATION_TABS_ITEMS[0].string

  const isAccountsDetailsPage =
    ssrWindow?.location?.pathname?.includes("user/dashboard")

  const getHeight = () => {
    if (!isGlobalNotificationClosed) {
      return isAccountsDashboardPage ? "285px" : "160px"
    }
    if (isGlobalNotificationClosed) {
      return isAccountsDashboardPage ? "180px" : "55px"
    }
    return "110px"
  }

  const height = getHeight()
  const { classes } = useStyles({
    height,
    isDashboardTabSelected,
  })

  const languageRoute = getLanguageRoute(siteLanguage.language)

  const isExternalLink =
    isMyWMApp || isTeamsitePage(ssrWindow.location.pathname)

  const damPath = getContentUrl(`/content/dam/wm/icons`)

  const newDashboardDamPath = getContentUrl(
    `/content/dam/wm/icons/single-use/dashboard`,
  )

  const [buttonPressed, setButtonPressed] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)

  useEffect(() => {
    if (isEmpty(multiAccountDashboard?.multiAccountDashboardTabSelected)) {
      setNewMultiAccountTabSelected?.(MULTI_NAVIGATION_TABS_ITEMS[0].string)
    }
  }, [])

  useEffect(() => {
    if (buttonPressed) {
      ssrWindow.document?.querySelector(`[aria-label="open menu"]`)?.focus()
    }
  }, [isMenuOpen, buttonPressed])

  const renderCart = () => {
    const numberOfProducts = get(ecommerceCart, `count`, ``)

    return (
      numberOfProducts && (
        <Button
          ariaLabel="Cart"
          className={classes.cartButton}
          onClick={onCartClick}
        >
          <ReactSVG
            wrapper="span"
            className={classes.cartIcon}
            src={`${newDashboardDamPath}/cart.svg`}
          />
          <span className={classes.counter}>{numberOfProducts}</span>
        </Button>
      )
    )
  }

  const renderMultiAccountNavigationTabs = () => {
    const languageRoute = getLanguageRoute(siteLanguage.language)

    return map(MULTI_NAVIGATION_TABS_ITEMS, (item) => (
      <Button
        tabIndex={0}
        fontClass={classes.headerButton}
        className={`w-50 ${
          item?.string === MULTI_NAVIGATION_TABS_ITEMS[0]?.string
            ? classes.dashboardTab
            : classes.billingTab
        }`}
        onClick={() => {
          setNewMultiAccountTabSelected(item.string)
          const isAccountsDashboardPage =
            ssrWindow?.location?.pathname?.includes("user/account-dashboard")
          if (!isAccountsDashboardPage) {
            const url = `${languageRoute}/mywm/user/account-dashboard`
            isMyWMApp ? router.push(url) : (ssrWindow.location = url)
          }
        }}
        data-testid="MobileRedesignedHeader-NavigationTabs"
      >
        {strings[item.string]}
      </Button>
    ))
  }

  const renderLogo = () => (
    <Link
      className={classes.wmLogo}
      href={logoLink}
      isExternalLink={isExternalLink}
    >
      <img
        className={classes.logo}
        src={getContentUrl(logo)}
        alt={logoAltText}
      />
    </Link>
  )

  const renderSupportButton = () => {
    const US_EN_ROUTE = `/us/en`

    let supportLinks = [
      {
        label: strings.SUPPORT,
        url: `${languageRoute}/support`,
        isExternalLink: true,
      },
    ]

    if (languageRoute === US_EN_ROUTE) {
      supportLinks = [
        ...supportLinks,
        {
          label: strings.MY_SUPPORT_REQUESTS,
          url: `${languageRoute}/mywm/user/my-support-requests`,
          isExternalLink: true,
        },
      ]
    }
    return (
      <SupportDropdown
        showCaret={false}
        label={
          <ReactSVG
            className={classes.icon}
            src={`${newDashboardDamPath}/support.svg`}
          />
        }
        links={supportLinks}
      />
    )
  }

  const renderAlertButton = () => (
    <Button
      className={classes.iconWrapper}
      onClick={() => {
        if (isTeamsitePage(ssrWindow.location.pathname) || !isMyWMApp) {
          ssrWindow.location.pathname = `${languageRoute}/mywm/user/notifications`
        } else {
          router.push(`${languageRoute}/mywm/user/notifications`)
        }
      }}
      ariaLabel="Notifications"
      testid="MobileRedesignedHeader-AlertButton"
    >
      <ReactSVG
        className={classes.icon}
        src={
          hasNotification
            ? `${newDashboardDamPath}/bell-notification.svg`
            : `${newDashboardDamPath}/bell-v2.svg`
        }
      />
    </Button>
  )

  const renderProfileButton = () => (
    <Button
      className={classes.iconWrapper}
      onClick={() => {
        setProfileMenuOpen(true)
      }}
      ariaLabel="Account"
      testid="MobileRedesignedHeader-ProfileButton"
    >
      <ReactSVG
        className={classes.icon}
        src={`${newDashboardDamPath}/account.svg`}
      />
    </Button>
  )

  const handleMenuClick = () => {
    onMenuClick(!isMenuOpen)
  }

  const renderMobileCustomerSelection = () => (
    <Box
      className={`${classes.customerSelectionRow} d-flex text-center justify-content-center align-items-center`}
    >
      <Box className={`${classes.dGrid} mx-3`}>
        <Typography
          variant="subheading2"
          className={`${classes.customerSelectionTextBold} w-100`}
        >
          {multiAccountDashboard?.selectedAccountDetails?.nickName ||
            multiAccountDashboard?.selectedAccountDetails?.custAccountId}
        </Typography>
        <Typography variant="body3">
          {getFormattedAddressWithoutPostalCode(
            multiAccountDashboard?.selectedAccountDetails,
          )}
        </Typography>
      </Box>
      <Box
        className="d-flex"
        onClick={() => setIsCustomerSelectClicked?.(true)}
      >
        {multiAccountDashboard?.isSelectCustomerDDClicked ? (
          <ChevronUp size={26} color={wmColor.black} />
        ) : (
          <ChevronDown size={26} color={wmColor.black} />
        )}
      </Box>
    </Box>
  )

  const renderDashboardTabs = () =>
    !isMenuOpen && (
      <Box className={classes.subHeaderContainer}>
        <Box className={classes.headerLinksWrapper}>
          {renderMultiAccountNavigationTabs()}
        </Box>
      </Box>
    )

  return (
    <>
      <Box className={classes.mobilePageWrapper} data-testid="MyWMHeaderMobile">
        <Box className={classes.headerContainer}>
          {isMenuOpen ? (
            <Box className={classes.openHamburgerHeaderWrapper}>
              {renderLogo()}
              <Box
                onClick={() => onMenuClick(!isMenuOpen)}
                data-testid="MyWMHeaderMobile-openHamburger"
                tabIndex="0"
                onKeyPress={(e) =>
                  handleEnterPress(e, () => {
                    handleMenuClick()
                  })
                }
                role="button"
                aria-label="close menu"
              >
                <ReactSVG
                  wrapper="span"
                  src={`${damPath}/green-close-x-icon.svg`}
                  className={classes.hamburgerOpenIcon}
                />
              </Box>
            </Box>
          ) : (
            <Box className={classes.closedHamburgerHeaderWrapper}>
              <Box className="d-flex align-items-center">
                <ReactSVG
                  wrapper="span"
                  className={classes.hamburgerClosedIcon}
                  src={`${damPath}/green-hamburger-menu-icon.svg`}
                  onClick={() => onMenuClick(!isMenuOpen)}
                  onKeyPress={(e) => {
                    setButtonPressed(true)
                    handleEnterPress(e, () => {
                      handleMenuClick()
                    })
                  }}
                  tabIndex="0"
                  role="button"
                  aria-label="open menu"
                />

                {renderLogo()}
              </Box>
              <Box
                className={`mobile-nav-container ${classes.rightMenuWrapper}`}
              >
                {renderSupportButton()}
                {renderCart()}
                {renderAlertButton()}
                {renderProfileButton()}
              </Box>
            </Box>
          )}
          {isAccountsDashboardPage &&
            !isSingleAccount &&
            !isMenuOpen &&
            renderMobileCustomerSelection()}
        </Box>
        <Box className="Header sub-header">
          {!isMenuOpen && (
            <GlobalNotifications
              alerts={alerts}
              onAlertsChange={onAlertsChange}
              onAlertClose={onAlertClose}
              isMobileRedesignedWMHeader
              renderDashboardTabs={renderDashboardTabs}
              isAccountsDashboardPage={isAccountsDashboardPage}
              isAccountsDetailsPage={isAccountsDetailsPage}
            />
          )}
          {isMenuOpen && (
            <>
              <SiteSearchMobileHeader
                isSearchOpen={showMobileSearch}
                isSupportSearch={isSupportSearch}
                setIsSearchOpen={setShowMobileSearch}
                indexList={indexList}
                resultsIndex={resultsIndex}
                suggestionIndex={suggestionIndex}
                queryIndex={queryIndex}
                searchPlaceholder={searchPlaceholder}
                numResults={numResults}
                numHighlightedResults={numHighlightedResults}
                numberQueries={numberQueries}
                numCharacters={numCharacters}
                errorMessageTitle={errorMessageTitle}
                errorMessageDescription={errorMessageDescription}
                failureTitle={failureTitle}
                failureDescription={failureDescription}
                suggestedText={suggestedText}
                resultsText={resultsText}
                seeAllResultsText={seeAllResultsText}
                searchDelay={searchDelay}
                language={siteLanguage.language}
              />
              <Box
                className={`MobileHeader__hamburger-menu w-100 position-absolute ${
                  isMenuOpen ? `MobileHeader__hamburger-menu--open` : ``
                }`}
              >
                <RedesignedHeaderNavItems
                  navigationDetails={navigationDetails}
                  onMenuClick={() => {
                    onMenuClick(false)
                  }}
                  setParentState={setParentState}
                  searchPlaceholder={strings.SEARCH}
                  setIsSearchOpen={setShowMobileSearch}
                  isHeaderMobile
                  isMenuOpen={isMenuOpen}
                />
              </Box>
            </>
          )}
        </Box>
        {profileMenuOpen && (
          <ProfileMenuModal
            firstName={firstName}
            emailId={emailId}
            mobilePhone={mobilePhone}
            selectedAccountDetails={selectedAccountDetails}
            profileMenuOpen={profileMenuOpen}
            setProfileMenuOpen={setProfileMenuOpen}
            siteLanguage={siteLanguage}
          />
        )}
      </Box>
    </>
  )
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// MobileRedesignedHeaderLegacy.propTypes = {
//   navigationDetails: PropTypes.array.isRequired,
//   siteLanguage: PropTypes.object.isRequired,
//   ecommerceCart: PropTypes.object,
//   isMenuOpen: PropTypes.bool.isRequired,
//   logo: PropTypes.string.isRequired,
//   logoLink: PropTypes.string.isRequired,
//   // this is an unused prop, removed from the migrated component
//   // language: PropTypes.string.isRequired,
//   onMenuClick: PropTypes.func.isRequired,
//   setParentState: PropTypes.func.isRequired,
//   setShowMobileSearch: PropTypes.func.isRequired,
//   showMobileSearch: PropTypes.bool.isRequired,
//   logoAltText: PropTypes.string.isRequired,
//   isSupportSearch: PropTypes.bool.isRequired,
//   indexList: PropTypes.array.isRequired,
//   suggestionIndex: PropTypes.string.isRequired,
//   queryIndex: PropTypes.string.isRequired,
//   numResults: PropTypes.number.isRequired,
//   numHighlightedResults: PropTypes.number.isRequired,
//   numberQueries: PropTypes.number.isRequired,
//   numCharacters: PropTypes.number.isRequired,
//   errorMessageTitle: PropTypes.string.isRequired,
//   errorMessageDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   resultsText: PropTypes.string.isRequired,
//   seeAllResultsText: PropTypes.string.isRequired,
//   searchDelay: PropTypes.number.isRequired,
//   resultsIndex: PropTypes.string.isRequired,
//   searchPlaceholder: PropTypes.string.isRequired,
//   hasNotification: PropTypes.bool.isRequired,
//   router: PropTypes.object.isRequired,
//   strings: PropTypes.object.isRequired,
//   onCartClick: PropTypes.func.isRequired,
//   alerts: PropTypes.array.isRequired,
//   onAlertsChange: PropTypes.func.isRequired,
//   onAlertClose: PropTypes.func.isRequired,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// MobileRedesignedHeaderLegacy.defaultProps = {
//   ecommerceCart: {},
// }

export default withLocalization(strings)(withRouter(MobileRedesignedHeader))
